"use client";

import { getLink } from "@utils/linkUtils";
import { useLocale, useTranslations } from "next-intl";
import Link from "next/link";
import React, { useContext, useEffect, useRef, useState } from "react";

import Icon from "@/components/ui/Icon/Icon";
import { ModalsContext } from "@/context/ModalsContext";
import useMediaQuery from "@/hooks/useMediaQuery";

import CreateLeadForm from "../CreateLeadModal/CreateLeadForm";
import Button, { ButtonSize, ButtonTheme, ButtonVariant } from "../ui/Button/Button";
import { EditorialModuleListIcon as EditorialModuleListIconType } from "./type";

export interface EditorialModuleListProps {
  entry: EditorialModuleListIconType;
}

function renderButtons(
  lines: EditorialModuleListIconType["lines"],
  locale: string,
  setEnquireModalOpen: (open: boolean) => void,
  enquireKeywords: string[],
  setGetFormPageLocation: (open: string) => void,
) {
  let hasEnquireShown = false;

  return lines?.map((cta, index) => {
    const isEnquireNow = enquireKeywords.some((keyword) => cta.link?.url?.includes(keyword));

    if (isEnquireNow) {
      if (hasEnquireShown) return null;
      hasEnquireShown = true;
      return null;
      // return (
      //   <button
      //     type="button"
      //     key={index}
      //     className="flex items-center gap-m"
      //     onClick={() => {
      //       setEnquireModalOpen(true);
      //       setGetFormPageLocation("SEO Sale Page Lead Form");
      //     }}
      //   >
      //     {cta.link.icon && <Icon size="24" name={cta.link.icon} className="stroke-black" />}
      //     <p className="font-medium font-secondary text-s">{cta.label}</p>
      //   </button>
      // );
    }

    const href = getLink(cta, locale);
    if (href) {
      return (
        <Link href={href} className="flex items-center gap-m" key={index}>
          {cta.link.icon && <Icon size="24" name={cta.link.icon} className="stroke-black" />}
          <p className="font-medium font-secondary text-s">{cta.label}</p>
        </Link>
      );
    }

    return (
      <div className="flex items-center gap-m" key={index}>
        {cta.link.icon && <Icon size="24" name={cta.link.icon} className="stroke-black" />}
        <p className="font-medium font-secondary text-s">{cta.label}</p>
      </div>
    );
  });
}

export default function EditorialModuleList({ entry }: EditorialModuleListProps) {
  const { stickyLeadForm = true } = entry;
  const locale = useLocale();
  const t = useTranslations();
  const { setGetInTouchModalOpen, setEnquireModalOpen, setGetFormPageLocation } = useContext(ModalsContext);
  const enquireKeywords = ["#enquireNow", "#callUs", "#contactUs"];
  const containerRef = useRef<HTMLDivElement>(null);
  const firstBlockRef = useRef<HTMLDivElement>(null);
  const secondBlockRef = useRef<HTMLDivElement>(null);
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const isMobile = useMediaQuery("(max-width: 600px)");
  const initialFirstBlockTopRef = useRef<number | null>(null);
  const [firstBlockStyle, setFirstBlockStyle] = useState<React.CSSProperties>({});
  const needsPushRef = useRef(false);
  const [leadFormPaddingTop, setLeadFormPaddingTop] = useState(0);

  useEffect(() => {
    const FIXED_TOP_OFFSET = window.innerWidth >= 1024 ? 120 : 88;

    const updateFirstBlockPosition = () => {
      // When content has been pushed below form, form scrolls naturally with the page
      if (needsPushRef.current) return;

      const containerEl = containerRef.current;
      const firstBlockEl = firstBlockRef.current;
      const secondBlockEl = secondBlockRef.current;

      if (!containerEl || !firstBlockEl || !secondBlockEl) return;

      const scrollY = window.scrollY;
      const containerRect = containerEl.getBoundingClientRect();
      const firstBlockRect = firstBlockEl.getBoundingClientRect();
      const secondBlockRect = secondBlockEl.getBoundingClientRect();

      const containerTop = containerRect.top + scrollY;
      const secondBlockTop = secondBlockRect.top + scrollY;
      if (initialFirstBlockTopRef.current === null) {
        initialFirstBlockTopRef.current = firstBlockEl.offsetTop;
      }
      const initialTop = initialFirstBlockTopRef.current;
      const formHeight = firstBlockRect.height;

      const startFixedAt = containerTop + initialTop - FIXED_TOP_OFFSET;
      const stopFixedAt = secondBlockTop - FIXED_TOP_OFFSET - formHeight - 60;

      if (scrollY < startFixedAt) {
        setFirstBlockStyle({
          position: "absolute",
          top: `${initialTop}px`,
          left: "45%",
          transform: "translateX(-50%)",
        });
        return;
      }

      if (scrollY >= startFixedAt && scrollY < stopFixedAt) {
        setFirstBlockStyle({
          position: "fixed",
          top: `${FIXED_TOP_OFFSET}px`,
          left: `${firstBlockRect.left}px`,
          width: `${firstBlockRect.width}px`,
          transform: "none",
          zIndex: 50,
        });
        return;
      }

      const lockedTop = secondBlockTop - containerTop - formHeight - 60;
      setFirstBlockStyle({
        position: "absolute",
        top: `${lockedTop}px`,
        left: "45%",
        transform: "translateX(-50%)",
      });
    };

    updateFirstBlockPosition();
    window.addEventListener("scroll", updateFirstBlockPosition, { passive: true });
    window.addEventListener("resize", updateFirstBlockPosition);

    return () => {
      window.removeEventListener("scroll", updateFirstBlockPosition);
      window.removeEventListener("resize", updateFirstBlockPosition);
    };
  }, []);

  // Measure whether content overlaps the form; if so, push it down and disable sticky
  useEffect(() => {
    if (!stickyLeadForm || !isDesktop) return;

    const measure = () => {
      const formEl = firstBlockRef.current;
      const contentEl = secondBlockRef.current;
      if (!formEl || !contentEl) return;

      if (contentEl.offsetTop < formEl.offsetHeight) {
        // Not enough gap — push content below form and let form scroll with page
        needsPushRef.current = true;
        setLeadFormPaddingTop(formEl.offsetTop + formEl.offsetHeight + 60 - contentEl.offsetTop);
        setFirstBlockStyle({});
      } else {
        // Enough gap — original sticky behaviour handles it
        needsPushRef.current = false;
        setLeadFormPaddingTop(0);
      }
    };

    measure();
    const observer = new ResizeObserver(measure);
    if (firstBlockRef.current) observer.observe(firstBlockRef.current);
    window.addEventListener("resize", measure);

    return () => {
      observer.disconnect();
      window.removeEventListener("resize", measure);
    };
  }, [stickyLeadForm, isDesktop]);

  const renderedButtons = renderButtons(entry.lines || [], locale, setEnquireModalOpen, enquireKeywords, setGetFormPageLocation);

  return (
    <div ref={containerRef} className="relative h-full flex flex-col md:justify-center px-m pt-l pb-[40px] md:pt-[40px]">
      {stickyLeadForm && isDesktop ? (
        <div
          ref={firstBlockRef}
          id="first-block"
          className="absolute top-xxl left-[45%] -translate-x-1/2 z-50 w-full max-w-[350px] rounded-[10px] bg-gray-300 p-m"
          style={{ backgroundColor: " #F8F7F7", ...firstBlockStyle }}
        >
          <div className="flex flex-col gap-s">
            <h2 className="font-primary font-medium text-m uppercase">{t("getInTouch.modal.title")}</h2>
            <p className="text-s font-secondary">{t("getInTouch.modal.subtitle")}</p>
            <CreateLeadForm mode="getInTouch" closeModal={() => {}} leadFormPageLocation="leadFormPageLocation" isStickleadForm={true} />
          </div>
        </div>
      ) : (
        isMobile && (
          <div className="start-chat-holder shadow-2xl" style={{ bottom: "174px", right: "13px", marginBottom: 0, zIndex: 60 }}>
            <Button
              rounded
              size={ButtonSize.MEDIUM}
              theme={ButtonTheme.GOLD}
              type="button"
              variant={ButtonVariant.PRIMARY}
              onClick={() => setGetInTouchModalOpen(true)}
            >
              <Icon size="24" name="envelope.svg" title="chat" className="" />
            </Button>
          </div>
        )
      )}

      <div
        ref={secondBlockRef}
        id="second-block"
        className="w-full max-w-[520px] mx-auto"
        style={leadFormPaddingTop > 0 ? { paddingTop: `${leadFormPaddingTop}px` } : { paddingTop: "80px" }}
      >
        <div className="pl-m benefitsContainer">
          {entry.title && <h3 className="font-medium font-primary text-s pb-l pt-xs">{entry.title}</h3>}
          {entry.textList && (
            <ul className="list-disc pl-m pb-xs">
              {entry.textList.map((item, index) => (
                <li className="font-secondary font-secondary-light text-m" key={index}>
                  {item}
                </li>
              ))}
            </ul>
          )}
          {entry.lines && <div className="flex flex-col gap-l py-xs">{renderedButtons}</div>}
        </div>
      </div>
    </div>
  );
}
